import { makeAutoObservable, runInAction } from "mobx";

import {
  getOperatorNotificationSnapshot,
  OperatorNotificationSnapshot,
} from "../axios/routes/operatorNotificationSnapshot";
import { PARAMS, Routes, gameHubPath, cameraMappingPath } from "../utils/routes";
import { clientTrackerDetailPath } from "../axios/routes/clientTracker";
import { normalizeOperatorEmail } from "../utils/operatorEmail";
import { NotificationServiceInstance } from "./notification";
import { SessionServiceInstance } from "./session";
import { playOperatorAdminChatIncomingChimeIfFresh } from "../utils/operatorAdminChatSound";

export type { OperatorNotificationSnapshot };

export type UnifiedFeedItemKind =
  | "checklist"
  | "new_account"
  | "support"
  | "draft_moment"
  | "draft_updated"
  | "game_assignment"
  | "admin_chat"
  | "client_tracker_stale"
  | "mapping_note";

export type UnifiedFeedItem = {
  id: string;
  kind: UnifiedFeedItemKind;
  title: string;
  description?: string;
  atMs: number;
  href?: string;
  /** Opens Admin Chat drawer to this thread when set. */
  adminChatThreadKey?: string;
  /** Latest admin-chat message id from snapshot; dedupes chime with socket. */
  adminChatSoundDedupeId?: string;
};

const DRAFT_LS_PREFIX = "momento-operator-draft-notifications-v1-";

const LAST_SEEN_PREFIX = "momento-operator-notifications-last-seen-";

const FEED_TOAST_IDS_PREFIX = "momento-operator-feed-toast-ids-v1-";

/** Poll notification snapshot while logged in (backup when socket misses updates). */
const NOTIFICATION_SNAPSHOT_POLL_MS = 5_000;

const MAX_PERSISTED_FEED_TOAST_IDS = 400;

const FEED_KINDS_WITH_LIVE_TOAST = new Set<UnifiedFeedItemKind>([
  "checklist",
  "new_account",
  "support",
  "game_assignment",
  "admin_chat",
  "client_tracker_stale",
  "mapping_note",
]);

function draftsStorageKey(emailNorm: string): string {
  return `${DRAFT_LS_PREFIX}${emailNorm}`;
}

function lastSeenStorageKey(emailNorm: string): string {
  return `${LAST_SEEN_PREFIX}${emailNorm}`;
}

type LocalDraft = {
  id: string;
  atMs: number;
  title: string;
  description?: string;
  gameId: string;
  generalMomentId: string;
  kind: "draft_moment" | "draft_updated";
};

function loadLocalDrafts(emailNorm: string): LocalDraft[] {
  if (!emailNorm) return [];
  try {
    const raw = localStorage.getItem(draftsStorageKey(emailNorm));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { drafts?: LocalDraft[] };
    return Array.isArray(parsed.drafts) ? parsed.drafts : [];
  } catch {
    return [];
  }
}

function saveLocalDrafts(emailNorm: string, drafts: LocalDraft[]): void {
  if (!emailNorm) return;
  localStorage.setItem(
    draftsStorageKey(emailNorm),
    JSON.stringify({ drafts: drafts.slice(0, 50) }),
  );
}

function momentsPath(gameId: string, generalMomentId: string): string {
  const p = new URLSearchParams();
  p.set(PARAMS.GAME_ID, gameId);
  p.set(PARAMS.OPEN_GENERAL_MOMENT_ID, generalMomentId);
  return `${Routes.MOMENT}?${p.toString()}`;
}

function feedToastIdsStorageKey(emailNorm: string): string {
  return `${FEED_TOAST_IDS_PREFIX}${emailNorm}`;
}

function loadPersistedFeedToastIds(emailNorm: string): string[] {
  if (!emailNorm) return [];
  try {
    const raw = sessionStorage.getItem(feedToastIdsStorageKey(emailNorm));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function persistFeedToastIds(emailNorm: string, ids: Set<string>): void {
  if (!emailNorm) return;
  const arr = Array.from(ids).slice(-MAX_PERSISTED_FEED_TOAST_IDS);
  sessionStorage.setItem(feedToastIdsStorageKey(emailNorm), JSON.stringify(arr));
}

export function buildSnapshotFeedItems(
  snap: OperatorNotificationSnapshot,
  viewerKey: string,
  isSuperAdmin: boolean,
): UnifiedFeedItem[] {
  const viewer = viewerKey || "";
  const base: UnifiedFeedItem[] = [
    ...snap.checklistFinishes.map((c): UnifiedFeedItem => {
      const atMs = Date.parse(c.submittedAt);
      const submitter = normalizeOperatorEmail(c.submittedByEmail);
      const own = Boolean(viewer && submitter === viewer);
      return {
        id: `checklist-${c.gameId}-${c.submittedAt}`,
        kind: "checklist",
        title: own ? "You completed a game checklist" : "Game checklist finished",
        description: own
          ? c.gameLabel
          : `${c.gameLabel} — submitted by ${
            c.submittedByDisplayName || c.submittedByEmail
          }`,
        atMs: Number.isFinite(atMs) ? atMs : 0,
        href: gameHubPath(c.gameId),
      };
    }),
    ...snap.newAccessRecords.map((a): UnifiedFeedItem => {
      const atMs = Date.parse(a.created);
      const label = a.name || a.firstName || a.email;
      const roleLabel = a.role === "superAdmin" ? "Super admin" : "Operator";
      return {
        id: `access-${a.email}-${a.created}`,
        kind: "new_account",
        title: "New operator account",
        description: `${label} (${a.email}) — ${roleLabel}`,
        atMs: Number.isFinite(atMs) ? atMs : 0,
        href: Routes.MANAGE_ACCESS,
      };
    }),
    ...snap.supportAssigned.map((x): UnifiedFeedItem => {
      const atMs = Date.parse(x.createdAt);
      const preview = x.messagePreview ? ` — ${x.messagePreview}` : "";
      return {
        id: `support-${x._id}`,
        kind: "support",
        title: "Support request assigned",
        description: `To ${x.assignedDisplayName || x.assigned}: ${x.fanEmail}${preview}`,
        atMs: Number.isFinite(atMs) ? atMs : 0,
        href: Routes.SUPPORT,
      };
    }),
    ...(snap.assignedGames || []).map((g): UnifiedFeedItem => {
      const raw = g.startDate;
      const atMs = typeof raw === "number" && Number.isFinite(raw)
        ? raw
        : 0;
      return {
        id: `assigned-game-${g.gameId}`,
        kind: "game_assignment",
        title: "You're assigned to a game",
        description: g.startTime
          ? `${g.gameLabel} - kickoff ${g.startTime}`
          : g.gameLabel,
        atMs,
        href: gameHubPath(g.gameId),
      };
    }),
  ];

  const adminRows = snap.adminChatAlerts || [];
  const adminItems: UnifiedFeedItem[] = adminRows.map((a): UnifiedFeedItem => {
    const atMs = Date.parse(a.lastMessageAt);
    const soundId = (a.lastMessageId && String(a.lastMessageId).trim())
      || `admin-chat-${a.threadKey}-${a.lastMessageAt}`;
    return {
      id: `admin-chat-${a.threadKey}-${a.lastMessageAt}`,
      kind: "admin_chat",
      title: isSuperAdmin ? "Operator admin chat" : "Super admin reply",
      description: isSuperAdmin
        ? `${a.operatorDisplayName || a.operatorEmail} — ${a.gameLabel} — ${a.preview}`
        : `${a.gameLabel} — ${a.preview}`,
      atMs: Number.isFinite(atMs) ? atMs : 0,
      adminChatThreadKey: a.threadKey,
      adminChatSoundDedupeId: soundId,
    };
  });

  const staleItems: UnifiedFeedItem[] = (snap.clientTrackerStaleContacts || []).map((row) => {
    const atMs = Date.parse(row.sentAt);
    const lastContact = row.lastClientContactAt
      ? `Last contact ${new Date(row.lastClientContactAt).toLocaleDateString()}`
      : "No qualifying client contact logged";
    return {
      id: `client-tracker-stale-${row.teamId}-${row.sentAt}`,
      kind: "client_tracker_stale",
      title: "Client needs contact",
      description: `${row.teamName} — ${lastContact}`,
      atMs: Number.isFinite(atMs) ? atMs : 0,
      href: clientTrackerDetailPath(row.teamId),
    };
  });

  const mappingNoteItems: UnifiedFeedItem[] = (snap.mappingNoteAlerts || []).map((row) => {
    const atMs = Date.parse(row.lastReplyAt);
    const who = row.replyAuthorName ? `${row.replyAuthorName} — ` : "";
    return {
      id: `mapping-note-${row.noteId}-${row.lastReplyAt}`,
      kind: "mapping_note",
      title: "Mapping sticky note reply",
      description: `${who}${row.cameraName}: ${row.preview}`,
      atMs: Number.isFinite(atMs) ? atMs : 0,
      href: cameraMappingPath({ cameraId: row.cameraId }),
    };
  });

  return [...base, ...adminItems, ...staleItems, ...mappingNoteItems];
}

function snapshotFeedItemsForViewer(
  snap: OperatorNotificationSnapshot | null,
  viewerKey: string,
): UnifiedFeedItem[] {
  if (!snap) return [];
  return buildSnapshotFeedItems(
    snap,
    viewerKey,
    SessionServiceInstance.isSuperAdmin,
  );
}

class OperatorNotificationFeedStore {
  snapshot: OperatorNotificationSnapshot | null = null;

  snapshotLoading = false;

  snapshotError: string | null = null;

  /** Normalized email for per-user local draft rows + last-seen. */
  activeViewerKey: string | null = null;

  localDrafts: LocalDraft[] = [];

  lastSeenAtMs = 0;

  /** First successful snapshot after login: seed ids without toasts. */
  private feedToastBaselineDone = false;

  /** Snapshot item ids we already showed a live toast for (or seeded). */
  private feedToastAnnouncedIds: Set<string> = new Set();

  private pollTimer: number | null = null;

  private pollViewerEmail: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /** Call when session email is known (login or refresh). */
  setActiveViewer(emailRaw: string | null | undefined): void {
    const key = normalizeOperatorEmail(emailRaw || "");
    if (!key) {
      this.stopNotificationPolling();
      this.activeViewerKey = null;
      this.localDrafts = [];
      this.lastSeenAtMs = 0;
      this.feedToastBaselineDone = false;
      this.feedToastAnnouncedIds = new Set();
      return;
    }
    if (this.activeViewerKey === key) {
      return;
    }
    this.activeViewerKey = key;
    this.localDrafts = loadLocalDrafts(key);
    const raw = localStorage.getItem(lastSeenStorageKey(key));
    const n = raw ? parseInt(raw, 10) : 0;
    this.lastSeenAtMs = Number.isFinite(n) ? n : 0;
    this.feedToastBaselineDone = false;
    this.feedToastAnnouncedIds = new Set(loadPersistedFeedToastIds(key));
  }

  get unreadCount(): number {
    // Ongoing assignment rows use kickoff time; excluding them avoids a huge badge on first visit.
    // Admin chat uses its own header badge and read state (see mark-read / chat drawer).
    return this.mergedItems.filter((row) => {
      return row.kind !== "game_assignment"
        && row.kind !== "admin_chat"
        && row.atMs > this.lastSeenAtMs;
    }).length;
  }

  get mergedItems(): UnifiedFeedItem[] {
    const viewer = this.activeViewerKey || "";
    const snap = this.snapshot;
    const fromSnap: UnifiedFeedItem[] = snapshotFeedItemsForViewer(snap, viewer);
    const fromLocal: UnifiedFeedItem[] = this.localDrafts.map((d) => ({
      id: d.id,
      kind: d.kind === "draft_updated" ? "draft_updated" : "draft_moment",
      title: d.title,
      description: d.description,
      atMs: d.atMs,
      href: momentsPath(d.gameId, d.generalMomentId),
    }));
    return [...fromSnap, ...fromLocal].sort((a, b) => b.atMs - a.atMs);
  }

  markNotificationsSeen(): void {
    if (!this.activeViewerKey) return;
    const now = Date.now();
    this.lastSeenAtMs = now;
    localStorage.setItem(lastSeenStorageKey(this.activeViewerKey), String(now));
  }

  async refreshSnapshot(viewerEmail: string): Promise<void> {
    const key = normalizeOperatorEmail(viewerEmail);
    if (key) {
      this.setActiveViewer(key);
    }
    runInAction(() => {
      this.snapshotLoading = true;
      this.snapshotError = null;
    });
    try {
      const data = await getOperatorNotificationSnapshot(viewerEmail);
      runInAction(() => {
        this.snapshot = data;
        this.snapshotLoading = false;
      });
      if (key) {
        this.processSnapshotForLiveToasts(key);
      }
    } catch (e: unknown) {
      const msg = e && typeof e === "object" && "message" in e
        ? String((e as { message: unknown }).message)
        : "Failed to refresh";
      runInAction(() => {
        this.snapshotError = msg;
        this.snapshotLoading = false;
      });
    }
  }

  private processSnapshotForLiveToasts(viewerKey: string): void {
    const snap = this.snapshot;
    if (!snap) return;

    const items = snapshotFeedItemsForViewer(snap, viewerKey);

    if (!this.feedToastBaselineDone) {
      items.forEach((i) => this.feedToastAnnouncedIds.add(i.id));
      this.feedToastBaselineDone = true;
      persistFeedToastIds(viewerKey, this.feedToastAnnouncedIds);
      return;
    }

    items.forEach((item) => {
      if (this.feedToastAnnouncedIds.has(item.id)) {
        return;
      }
      this.feedToastAnnouncedIds.add(item.id);
      if (FEED_KINDS_WITH_LIVE_TOAST.has(item.kind)) {
        const isAdminChat = item.kind === "admin_chat" && item.adminChatThreadKey;
        if (isAdminChat) {
          playOperatorAdminChatIncomingChimeIfFresh(
            item.adminChatSoundDedupeId || item.id,
          );
        }
        NotificationServiceInstance.showOperatorFeedItemNotification({
          title: item.title,
          description: item.description,
          href: isAdminChat ? undefined : item.href,
          onClick: isAdminChat
            ? () => {
              window.dispatchEvent(
                new CustomEvent("momento:open-admin-chat", {
                  detail: { threadKey: item.adminChatThreadKey },
                }),
              );
            }
            : undefined,
          actionLabel: isAdminChat ? "Open chat" : undefined,
        });
      }
    });
    persistFeedToastIds(viewerKey, this.feedToastAnnouncedIds);
  }

  startNotificationPolling(viewerEmail: string): void {
    this.stopNotificationPolling();
    const norm = normalizeOperatorEmail(viewerEmail);
    if (!norm || !viewerEmail) return;
    this.pollViewerEmail = viewerEmail;
    this.pollTimer = window.setInterval(() => {
      if (this.pollViewerEmail) {
        this.refreshSnapshot(this.pollViewerEmail).catch(() => undefined);
      }
    }, NOTIFICATION_SNAPSHOT_POLL_MS);
  }

  stopNotificationPolling(): void {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.pollViewerEmail = null;
  }

  recordDraftMomentCaptured(params: {
    gameLabel: string;
    gameId: string;
    generalMomentId: string;
  }): void {
    const key = normalizeOperatorEmail(SessionServiceInstance.EmailAddress || "");
    if (!key) return;
    this.setActiveViewer(key);
    const atMs = Date.now();
    const next: LocalDraft = {
      id: `draft-new-${params.generalMomentId}-${atMs}`,
      atMs,
      kind: "draft_moment",
      title: "New draft moment",
      description: `${params.gameLabel} — ready to label`,
      gameId: params.gameId,
      generalMomentId: params.generalMomentId,
    };
    const merged = [next, ...loadLocalDrafts(key)].slice(0, 50);
    saveLocalDrafts(key, merged);
    this.localDrafts = merged;
  }

  recordDraftMomentUpdated(params: {
    gameLabel: string;
    gameId: string;
    generalMomentId: string;
    cameraName?: string;
  }): void {
    const key = normalizeOperatorEmail(SessionServiceInstance.EmailAddress || "");
    if (!key) return;
    this.setActiveViewer(key);
    const atMs = Date.now();
    const next: LocalDraft = {
      id: `draft-upd-${params.generalMomentId}-${atMs}`,
      atMs,
      kind: "draft_updated",
      title: "Draft moment updated",
      description: params.cameraName
        ? `${params.gameLabel} — new images (${params.cameraName})`
        : `${params.gameLabel} — new images`,
      gameId: params.gameId,
      generalMomentId: params.generalMomentId,
    };
    const merged = [next, ...loadLocalDrafts(key)].slice(0, 50);
    saveLocalDrafts(key, merged);
    this.localDrafts = merged;
  }
}

export const OperatorNotificationFeedInstance = new OperatorNotificationFeedStore();
